import React from "react";
import { Image, Upload, Edit, Save } from "lucide-react";

export default function AdminProducts({
  newProduct,
  setNewProduct,
  addProduct,
  products,
  renderDashboardPricingPreview,
  statusFilter,
  setStatusFilter,
  editProduct,
  setEditProduct,
  setEditProductSlug,
  startEditProduct,
  saveEditProduct,
  toggleProduct,
  delProduct,
  handleUpload,
  handleFileDrop,
  handleImageArrayUpload,
  removeImageAt,
  handleImageArrayFile,
  handleUploadFile,
  handleVariantImageFile,
  handleVariantImageUpload,
  handleVariantImageDrop,
  removeVariantImage,
  variantImageFor,
  fixedProductType,
}) {
  const csv = (value) =>
    String(value || "")
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);

  // Gift fields are configured as: Name [required]; Message [textarea]; Colour [select: Red|Blue|Black].
  // Keep the existing structured schema while making it editable without JSON.
  const parseGiftOptions = (value) =>
    String(value || "")
      .split(";")
      .map((entry) => entry.trim())
      .filter(Boolean)
      .map((entry) => {
        const directives = [];
        const label = entry.replace(/\s*\[[^\]]+\]\s*/g, (directive) => {
          directives.push(directive.slice(directive.indexOf("[") + 1, directive.lastIndexOf("]")).trim());
          return " ";
        }).trim();
        const selectDirective = directives.find((directive) => /^select\s*:/i.test(directive));
        const keyDirective = directives.find((directive) => /^key\s*:/i.test(directive));
        const type = selectDirective ? "select" : directives.some((directive) => /^textarea$/i.test(directive)) ? "textarea" : "text";
        const choices = selectDirective ? selectDirective.replace(/^select\s*:/i, "").split("|").map((choice) => choice.trim()) : [];
        const generatedName = label.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
        const explicitName = keyDirective ? keyDirective.replace(/^key\s*:/i, "").trim() : "";
        const name = keyDirective ? explicitName : generatedName;
        return { name, label, type, required: directives.some((directive) => /^required$/i.test(directive)), ...(type === "select" ? { choices } : {}) };
      });

  const formatGiftOptions = (options) =>
    (Array.isArray(options) ? options : [])
      .map((option) => {
        const label = option.label || option.name || "";
        const directives = [];
        const generatedName = label.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
        const optionName = String(option.name || generatedName);
        if (/^[a-zA-Z0-9_]+$/.test(optionName) && optionName !== generatedName) directives.push(`key: ${optionName}`);
        if (option.type === "select") directives.push(`select: ${(option.choices || []).join("|")}`);
        else if (option.type === "textarea") directives.push("textarea");
        if (option.required) directives.push("required");
        return directives.length ? `${label} ${directives.map((directive) => `[${directive}]`).join(" ")}` : label;
      })
      .join("; ");

  return (
    <>
      {/* =========================
          PRODUCTS
      ========================= */}
        <div
          className="mt-6"
          data-testid="admin-products"
        >
          <form
            onSubmit={addProduct}
            className="card-pmt p-4 grid grid-cols-1 md:grid-cols-3 gap-3"
          >
            <div>
              <div className="label-pmt">
                Slug
              </div>

              <input
                required
                className="input-pmt"
                placeholder="e.g. running-tees"
                value={
                  newProduct.slug
                }
                onChange={(e) =>
                  setNewProduct({
                    ...newProduct,
                    slug: e.target.value,
                  })
                }
              />
            </div>

            <div>
              <div className="label-pmt">
                Name
              </div>

              <input
                required
                className="input-pmt"
                value={
                  newProduct.name
                }
                onChange={(e) =>
                  setNewProduct({
                    ...newProduct,
                    name: e.target.value,
                  })
                }
              />
            </div>

            <div>
              <div className="label-pmt">
                Category
              </div>

              <input
                required
                className="input-pmt"
                value={
                  newProduct.category
                }
                onChange={(e) =>
                  setNewProduct({
                    ...newProduct,
                    category:
                      e.target.value,
                  })
                }
              />
            </div>

            <div>
              <div className="label-pmt">Product Type</div>
              <select className="input-pmt" value={newProduct.product_type || "apparel"} onChange={(e) => setNewProduct({ ...newProduct, product_type: e.target.value })}>
                <option value="apparel">Apparel</option>
                <option value="gift">Personalised Gift</option>
              </select>
            </div>
            <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={!!newProduct.featured} disabled={newProduct.product_type === "gift" && !newProduct.featured && products.filter((p) => p?.product_type === "gift" && p?.active !== false && p?.featured === true).length >= 6} onChange={(e) => setNewProduct({ ...newProduct, featured: e.target.checked })} /> {newProduct.product_type === "gift" ? "Show in homepage Personalised Gifts (max 6)" : "Show on homepage (Picked for You)"}</label>
            {newProduct.product_type === "gift" && (
              <div className="md:col-span-2">
                <div className="label-pmt">Gift personalisation fields</div>
                <textarea className="input-pmt min-h-[76px]" value={formatGiftOptions(newProduct.gift_options)} onChange={(e) => setNewProduct({ ...newProduct, gift_options: parseGiftOptions(e.target.value) })} placeholder="Name [required]; Message [textarea]; Colour [select: Red|Blue|Black]" />
                <div className="text-[10px] text-stone-500 mt-1">Separate fields with semicolons. Add [required], [textarea], or [select: Option A|Option B]. Existing field keys are preserved automatically. Maximum 20 fields.</div>
              </div>
            )}

            <div className="md:col-span-3">
              <div className="label-pmt">
                Short Description
              </div>

              <input
                required
                className="input-pmt"
                value={
                  newProduct.description
                }
                onChange={(e) =>
                  setNewProduct({
                    ...newProduct,
                    description:
                      e.target.value,
                  })
                }
              />
            </div>

            {newProduct.product_type !== "gift" && (<>
            <div>
              <div className="label-pmt">
                Sizes (comma separated)
              </div>

              <input
                className="input-pmt"
                value={
                  newProduct.sizes
                }
                onChange={(e) =>
                  setNewProduct({
                    ...newProduct,
                    sizes:
                      e.target.value,
                  })
                }
              />
            </div>

            <div>
              <div className="label-pmt">
                Colours (comma separated)
              </div>

              <input
                className="input-pmt"
                value={
                  newProduct.colors
                }
                onChange={(e) =>
                  setNewProduct({
                    ...newProduct,
                    colors:
                      e.target.value,
                  })
                }
              />
            </div>

            <div>
              <div className="label-pmt">Quality / GSM + Price</div>
              <input
                className="input-pmt"
                value={newProduct.qualities}
                onChange={(e) => setNewProduct({ ...newProduct, qualities: e.target.value })}
                placeholder="180 GSM | 399, 240 GSM | 449"
              />
              <div className="text-[10px] text-stone-500 mt-1">Format: GSM | final base price. Example: 180 GSM | 399, 240 GSM | 449.</div>
            </div>

            </>)}
            {renderDashboardPricingPreview(newProduct.category)}

            {newProduct.product_type !== "gift" && (<>
            <div>
              <div className="label-pmt">
                Printing (comma separated)
              </div>

              <input
                className="input-pmt"
                value={
                  newProduct.printing_methods
                }
                onChange={(e) =>
                  setNewProduct({
                    ...newProduct,
                    printing_methods:
                      e.target.value,
                  })
                }
              />
            </div>

            </>)}
            <div className="md:col-span-2">
              <div className="label-pmt">
                Image
              </div>

              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  className="input-pmt flex-1"
                  placeholder="Paste image URL"
                  value={
                    newProduct.image
                  }
                  onChange={(e) =>
                    setNewProduct({
                      ...newProduct,
                      image:
                        e.target.value,
                    })
                  }
                />

                <label className="btn-outline-pmt cursor-pointer text-xs" onDragOver={(e) => e.preventDefault()} onDragEnter={(e) => e.currentTarget.classList.add("ring-2", "ring-black", "ring-offset-2")} onDragLeave={(e) => e.currentTarget.classList.remove("ring-2", "ring-black", "ring-offset-2")} onDrop={handleFileDrop((file) => handleUploadFile(file, "image", setNewProduct))}>
                  <Upload
                    size={14}
                  />
                  Upload

                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleUpload(
                      "image",
                      setNewProduct
                    )}
                  />
                </label>
              </div>

              {newProduct.image && (
                <img
                  src={
                    newProduct.image
                  }
                  alt="preview"
                  className="mt-2 w-20 h-20 object-cover border border-[#E6DFD5]"
                />
              )}
            </div>

            {newProduct.product_type !== "gift" && (<>
            <div>
              <div className="label-pmt">
                Styles / Variants (comma separated)
              </div>
              <input
                className="input-pmt"
                value={newProduct.styles}
                onChange={(e) =>
                  setNewProduct({
                    ...newProduct,
                    styles: e.target.value,
                  })
                }
                placeholder="e.g. Round Neck, Collar, Full Sleeve"
              />
              <div className="text-[10px] text-stone-500 mt-1">
                Example: Round Neck, Collar, Full Sleeve.
              </div>
            </div>

            <div className="md:col-span-3 border border-[#E6DFD5] p-4 bg-[#F8F5F0]">
              <div className="label-pmt">Variant Images</div>
              <div className="text-[10px] text-stone-500 mt-1 mb-3">
                Upload an image for each Style + Colour combination. Leave combinations without a dedicated image empty.
              </div>
              {csv(newProduct.styles).length > 0 && csv(newProduct.colors).length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs border-collapse">
                    <thead>
                      <tr>
                        <th className="text-left p-2 border-b">Style</th>
                        {csv(newProduct.colors).map((color) => (
                          <th key={color} className="text-left p-2 border-b whitespace-nowrap">{color}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {csv(newProduct.styles).map((style) => (
                        <tr key={style}>
                          <td className="p-2 border-b font-semibold whitespace-nowrap">{style}</td>
                          {csv(newProduct.colors).map((color) => {
                            const image = variantImageFor(newProduct.variant_images, style, color);
                            return (
                              <td key={color} className="p-2 border-b align-top min-w-[110px]">
                                <div className="w-20 h-20 bg-white border border-[#E6DFD5]">
                                  {image ? (
                                    <img src={image} alt={style + " " + color} className="w-full h-full object-cover" />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center text-[9px] text-stone-400">No image</div>
                                  )}
                                </div>
                                <label className="mt-1 block text-[10px] uppercase font-semibold cursor-pointer" onDragOver={(e) => e.preventDefault()} onDragEnter={(e) => e.currentTarget.classList.add("ring-2", "ring-black", "ring-offset-2")} onDragLeave={(e) => e.currentTarget.classList.remove("ring-2", "ring-black", "ring-offset-2")} onDrop={handleVariantImageDrop(style, color, setNewProduct)}>
                                  {image ? "Replace" : "Upload"}
                                  <input
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={handleVariantImageUpload(style, color, setNewProduct)}
                                  />
                                </label>
                                {image && (
                                  <button
                                    type="button"
                                    className="text-[10px] text-red-600"
                                    onClick={() => removeVariantImage(style, color, setNewProduct)}
                                  >
                                    Remove
                                  </button>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-xs text-stone-500">
                  Add at least one style and one colour above to upload variant images.
                </div>
              )}
            </div>

            </>)}
            <div className="md:col-span-2">
              <div className="label-pmt">
                Product Images (up to 5)
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-2">
                {Array.from({ length: 5 }).map((_, index) => {
                  const image = (newProduct.images || [])[index];
                  return (
                    <div key={index} className="border border-[#E6DFD5] p-2 bg-white" onDragOver={(e) => e.preventDefault()} onDragEnter={(e) => e.currentTarget.classList.add("ring-2", "ring-black", "ring-offset-2")} onDragLeave={(e) => e.currentTarget.classList.remove("ring-2", "ring-black", "ring-offset-2")} onDrop={handleFileDrop((file) => handleImageArrayFile(file, index, setNewProduct))}>
                      {image ? (
                        <img
                          src={image}
                          alt={`View ${index + 1}`}
                          className="w-full aspect-square object-cover"
                        />
                      ) : (
                        <div className="w-full aspect-square flex items-center justify-center text-[10px] text-stone-400 bg-[#F8F5F0]">
                          View {index + 1}
                        </div>
                      )}
                      <label className="mt-2 block text-center text-[10px] uppercase font-semibold cursor-pointer" onDragOver={(e) => e.preventDefault()} onDragEnter={(e) => e.currentTarget.classList.add("ring-2", "ring-black", "ring-offset-2")} onDragLeave={(e) => e.currentTarget.classList.remove("ring-2", "ring-black", "ring-offset-2")} onDrop={handleFileDrop((file) => handleImageArrayFile(file, index, setNewProduct))}>
                        {image ? "Replace" : "Upload"}
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={handleImageArrayUpload(index, setNewProduct)}
                        />
                      </label>
                      {image && (
                        <button
                          type="button"
                          className="mt-1 w-full text-[10px] text-red-600"
                          onClick={() => removeImageAt(index, setNewProduct)}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="text-[10px] text-stone-500 mt-1">
                Add up to 5 product photos. The first image is used as the main gallery image.
              </div>
            </div>

<div>
              <div className="label-pmt">
                Price (₹, leave empty for Get Quote)
              </div>

              <input
                className="input-pmt"
                placeholder="e.g. 499"
                value={
                  newProduct.price
                }
                onChange={(e) =>
                  setNewProduct({
                    ...newProduct,
                    price:
                      e.target.value,
                  })
                }
              />
            </div>

            <div>
              <div className="label-pmt">Discount (%)</div>
              <input type="number" min="0" max="100" step="1" className="input-pmt" placeholder="0"
                value={newProduct.discount_percent}
                onChange={(e) => setNewProduct({ ...newProduct, discount_percent: e.target.value })} />
              <div className="text-[10px] text-stone-500 mt-1">Applies to the base price and each GSM price.</div>
            </div>

            <div>
              <div className="label-pmt">
                HSN Code
              </div>
              <input
                className="input-pmt"
                placeholder="e.g. 6109"
                value={newProduct.hsn_code}
                onChange={(e) =>
                  setNewProduct({
                    ...newProduct,
                    hsn_code: e.target.value,
                  })
                }
              />
            </div>

            <div>
              <div className="label-pmt">
                GST Rate (%)
              </div>
              <input
                type="number"
                min="0"
                step="0.01"
                className="input-pmt"
                placeholder="Auto (5 / 12)"
                value={newProduct.gst_rate}
                onChange={(e) =>
                  setNewProduct({
                    ...newProduct,
                    gst_rate: e.target.value,
                  })
                }
              />
            </div>

            <div className="md:col-span-3">
              <button
                className="btn-primary"
                type="submit"
              >
                Add Product
              </button>
            </div>
          </form>

          <div className="mt-4 flex flex-wrap gap-3 items-center">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="input-pmt max-w-xs"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="disabled">Disabled</option>
            </select>
            <span className="text-xs font-mono uppercase text-stone-500">
              {products.length} products
            </span>
          </div>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            {products.map(
              (p) => (
                <div
                  key={p.slug}
                  className="card-pmt p-4 flex gap-3"
                >
                  {p.image && (
                    <img
                      src={p.image}
                      alt={p.name}
                      className="w-20 h-20 object-cover border border-[#E6DFD5]"
                    />
                  )}

                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <div className="font-bold">
                        {
                          p.name
                        }
                      </div>

                      <span
                        className={`text-[10px] uppercase font-mono ${
                          p.active ===
                          false
                            ? "text-red-600"
                            : "text-green-700"
                        }`}
                      >
                        {p.active ===
                        false
                          ? "hidden"
                          : "active"}
                      </span>
                    </div>

                    <div className="text-xs text-stone-500">
                      {
                        p.category
                      }{" "}
                      ·{" "}
                      {
                        p.slug
                      }
                    </div>

                    {p.product_type !== "gift" && (
                      <div className="text-xs mt-1">
                        Sizes:{" "}
                        {(
                          p.sizes ||
                          []
                        ).join(
                          ", "
                        )}
                      </div>
                    )}

                    <div className="text-xs">
                      Price:{" "}
                      {p.price !=
                      null
                        ? `₹${p.price}`
                        : "Get Quote"}
                    </div>

                    <div className="mt-2 flex flex-wrap gap-3 text-xs">
                      <button
                        onClick={() =>
                          startEditProduct(
                            p
                          )
                        }
                        className="underline"
                      >
                        Edit
                      </button>

                      <button
                        onClick={() =>
                          toggleProduct(
                            p
                          )
                        }
                        className="underline"
                      >
                        {p.active ===
                        false
                          ? "Enable"
                          : "Disable"}
                      </button>

                      <button
                        onClick={() =>
                          delProduct(
                            p.slug
                          )
                        }
                        className="text-red-600 underline"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              )
            )}
          </div>

          {/* EDIT PRODUCT */}

          {editProduct && (
            <div
              className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
              onClick={() => {
                setEditProductSlug(null);
                setEditProduct(null);
              }}
            >
              <div
                className="bg-white max-w-3xl w-full p-6 max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between gap-4 mb-5">
                  <div>
                    <div className="overline">Edit Product</div>
                    <div className="font-bold text-lg mt-1">
                      {editProduct.name || editProductSlug}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setEditProductSlug(null);
                      setEditProduct(null);
                    }}
                    className="btn-outline-pmt text-xs"
                  >
                    Close
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <div className="label-pmt">Name</div>
                    <input
                      className="input-pmt"
                      value={editProduct.name || ""}
                      onChange={(e) =>
                        setEditProduct({ ...editProduct, name: e.target.value })
                      }
                    />
                  </div>

                  <div>
                    <div className="label-pmt">Category</div>
                    <input
                      className="input-pmt"
                      value={editProduct.category || ""}
                      onChange={(e) =>
                        setEditProduct({ ...editProduct, category: e.target.value })
                      }
                    />
                  </div>

                  <div>
                    <div className="label-pmt">Product Type</div>
                    <select
                      className="input-pmt"
                      value={fixedProductType || editProduct.product_type || "apparel"}
                      disabled={!!fixedProductType}
                      onChange={(e) => setEditProduct({ ...editProduct, product_type: e.target.value })}
                    >
                      <option value="apparel">Apparel</option>
                      <option value="gift">Personalised Gift</option>
                    </select>
                    {fixedProductType && <div className="text-[10px] text-stone-500 mt-1">Product type is fixed by the current tab.</div>}
                  </div>
                  {(fixedProductType || editProduct.product_type) === "gift" && (
                    <div className="sm:col-span-2">
                      <div className="label-pmt">Gift personalisation fields</div>
                      <textarea className="input-pmt min-h-[76px]" value={formatGiftOptions(editProduct.gift_options)} onChange={(e) => setEditProduct({ ...editProduct, gift_options: parseGiftOptions(e.target.value) })} placeholder="Name [required]; Message [textarea]; Colour [select: Red|Blue|Black]" />
                      <div className="text-[10px] text-stone-500 mt-1">Separate fields with semicolons. Add [required], [textarea], or [select: Option A|Option B]. Existing field keys are preserved automatically. Maximum 20 fields.</div>
                    </div>
                  )}

                  <div className="sm:col-span-2">
                    <div className="label-pmt">Description</div>
                    <input
                      className="input-pmt"
                      value={editProduct.description || ""}
                      onChange={(e) =>
                        setEditProduct({ ...editProduct, description: e.target.value })
                      }
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <div className="label-pmt">Long Description</div>
                    <textarea
                      rows={3}
                      className="input-pmt"
                      value={editProduct.long_description || ""}
                      onChange={(e) =>
                        setEditProduct({
                          ...editProduct,
                          long_description: e.target.value,
                        })
                      }
                    />
                  </div>

                  {editProduct.product_type !== "gift" && (<>
                  <div>
                    <div className="label-pmt">Sizes (comma separated)</div>
                    <input
                      className="input-pmt"
                      value={editProduct.sizes || ""}
                      onChange={(e) =>
                        setEditProduct({ ...editProduct, sizes: e.target.value })
                      }
                    />
                  </div>

                  <div>
                    <div className="label-pmt">Colours (comma separated)</div>
                    <input
                      className="input-pmt"
                      value={editProduct.colors || ""}
                      onChange={(e) =>
                        setEditProduct({ ...editProduct, colors: e.target.value })
                      }
                    />
                  </div>

                  <div>
                    <div className="label-pmt">Styles / Variants (comma separated)</div>
                    <input className="input-pmt" value={editProduct.styles || ""} onChange={(e) => setEditProduct({ ...editProduct, styles: e.target.value })} placeholder="e.g. Round Neck, Collar, Full Sleeve" />
                  </div>

                  <div>
                    <div className="label-pmt">Quality / GSM + Price</div>
                    <input className="input-pmt" value={editProduct.qualities || ""} onChange={(e) => setEditProduct({ ...editProduct, qualities: e.target.value })} placeholder="180 GSM | 399, 240 GSM | 449" />
                    <div className="text-[10px] text-stone-500 mt-1">Format: GSM | final base price. Printing/personalisation charges remain separate.</div>
                  </div>

                  </>)}
                  {renderDashboardPricingPreview(editProduct.category)}

                  {editProduct.product_type !== "gift" && (<>
                  <div>
                    <div className="label-pmt">Printing (comma separated)</div>
                    <input
                      className="input-pmt"
                      value={editProduct.printing_methods || ""}
                      onChange={(e) =>
                        setEditProduct({
                          ...editProduct,
                          printing_methods: e.target.value,
                        })
                      }
                    />
                  </div>

                  </>)}
                  <div>
                    <div className="label-pmt">Price (₹)</div>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      className="input-pmt"
                      value={editProduct.price ?? ""}
                      onChange={(e) =>
                        setEditProduct({ ...editProduct, price: e.target.value })
                      }
                    />
                    <div className="text-[10px] text-stone-500 mt-1">
                      Leave empty to show Get Quote.
                    </div>
                  </div>

                  <div>
                    <div className="label-pmt">Discount (%)</div>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="1"
                      className="input-pmt"
                      value={editProduct.discount_percent ?? 0}
                      onChange={(e) =>
                        setEditProduct({ ...editProduct, discount_percent: e.target.value })
                      }
                    />
                    <div className="text-[10px] text-stone-500 mt-1">
                      Applies to the base price and each GSM price.
                    </div>
                  </div>

                  <div>
                    <div className="label-pmt">HSN Code</div>
                    <input
                      className="input-pmt"
                      placeholder="e.g. 6109"
                      value={editProduct.hsn_code || ""}
                      onChange={(e) =>
                        setEditProduct({ ...editProduct, hsn_code: e.target.value })
                      }
                    />
                  </div>

                  <div>
                    <div className="label-pmt">GST Rate (%)</div>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className="input-pmt"
                      placeholder="Auto (5 / 12)"
                      value={editProduct.gst_rate ?? ""}
                      onChange={(e) =>
                        setEditProduct({ ...editProduct, gst_rate: e.target.value })
                      }
                    />
                  </div>

                  {editProduct.product_type !== "gift" && (<>
                  <div className="sm:col-span-2 border border-[#E6DFD5] p-4 bg-[#F8F5F0]">
                    <div className="label-pmt">Variant Images</div>
                    <div className="text-[10px] text-stone-500 mt-1 mb-3">Upload an image for each Style + Colour combination. Leave combinations without a dedicated image empty.</div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs border-collapse">
                        <thead><tr><th className="text-left p-2 border-b">Style</th>{csv(editProduct.colors).map((color) => <th key={color} className="text-left p-2 border-b whitespace-nowrap">{color}</th>)}</tr></thead>
                        <tbody>{csv(editProduct.styles).map((style) => <tr key={style}><td className="p-2 border-b font-semibold whitespace-nowrap">{style}</td>{csv(editProduct.colors).map((color) => { const image = variantImageFor(editProduct.variant_images, style, color); return <td key={color} className="p-2 border-b align-top min-w-[110px]"><div className="w-20 h-20 bg-white border border-[#E6DFD5]">{image ? <img src={image} alt={style + " " + color} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-[9px] text-stone-400">No image</div>}</div><label className="mt-1 block text-[10px] uppercase font-semibold cursor-pointer" onDragOver={(e) => e.preventDefault()} onDragEnter={(e) => e.currentTarget.classList.add("ring-2", "ring-black", "ring-offset-2")} onDragLeave={(e) => e.currentTarget.classList.remove("ring-2", "ring-black", "ring-offset-2")} onDrop={handleVariantImageDrop(style, color, setEditProduct)}>{image ? "Replace" : "Upload"}<input type="file" accept="image/*" className="hidden" onChange={handleVariantImageUpload(style, color, setEditProduct)} /></label>{image && <button type="button" className="text-[10px] text-red-600" onClick={() => removeVariantImage(style, color, setEditProduct)}>Remove</button>}</td>; })}</tr>)}</tbody>
                      </table>
                    </div>
                  </div>

                  </>)}
                  <div className="sm:col-span-2">
                    <div className="label-pmt">Main Product Image</div>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <input
                        className="input-pmt flex-1"
                        placeholder="Paste image URL"
                        value={editProduct.image || ""}
                        onChange={(e) =>
                          setEditProduct({ ...editProduct, image: e.target.value })
                        }
                      />
                      <label className="btn-outline-pmt cursor-pointer text-xs" onDragOver={(e) => e.preventDefault()} onDragEnter={(e) => e.currentTarget.classList.add("ring-2", "ring-black", "ring-offset-2")} onDragLeave={(e) => e.currentTarget.classList.remove("ring-2", "ring-black", "ring-offset-2")} onDrop={handleFileDrop((file) => handleUploadFile(file, "image", setEditProduct))}>
                        <Upload size={14} />
                        Upload
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={handleUpload("image", setEditProduct)}
                        />
                      </label>
                    </div>
                    {editProduct.image && (
                      <img
                        src={editProduct.image}
                        alt="Product preview"
                        className="mt-2 w-24 h-24 object-cover border border-[#E6DFD5]"
                      />
                    )}
                  </div>

                  <div className="sm:col-span-2">
                    <div className="label-pmt">Product Images (up to 5)</div>
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-2">
                      {Array.from({ length: 5 }).map((_, index) => {
                        const image = (editProduct.images || [])[index];
                        return (
                          <div
                            key={index}
                            className="border border-[#E6DFD5] p-2 bg-white"
                            onDragOver={(e) => e.preventDefault()} onDragEnter={(e) => e.currentTarget.classList.add("ring-2", "ring-black", "ring-offset-2")} onDragLeave={(e) => e.currentTarget.classList.remove("ring-2", "ring-black", "ring-offset-2")} onDrop={handleFileDrop((file) => handleImageArrayFile(file, index, setEditProduct))}
                          >
                            {image ? (
                              <img
                                src={image}
                                alt={`View ${index + 1}`}
                                className="w-full aspect-square object-cover"
                              />
                            ) : (
                              <div className="w-full aspect-square flex items-center justify-center text-[10px] text-stone-400 bg-[#F8F5F0]">
                                View {index + 1}
                              </div>
                            )}
                            <label className="mt-2 block text-center text-[10px] uppercase font-semibold cursor-pointer" onDragOver={(e) => e.preventDefault()} onDragEnter={(e) => e.currentTarget.classList.add("ring-2", "ring-black", "ring-offset-2")} onDragLeave={(e) => e.currentTarget.classList.remove("ring-2", "ring-black", "ring-offset-2")} onDrop={handleFileDrop((file) => handleImageArrayFile(file, index, setEditProduct))}>
                              {image ? "Replace" : "Upload"}
                              <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={handleImageArrayUpload(index, setEditProduct)}
                              />
                            </label>
                            {image && (
                              <button
                                type="button"
                                className="mt-1 w-full text-[10px] text-red-600"
                                onClick={() => removeImageAt(index, setEditProduct)}
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <div className="text-[10px] text-stone-500 mt-1">
                      Add up to 5 product photos. The first image is used as the main gallery image.
                    </div>
                  </div>

                  <div className="sm:col-span-2 flex flex-col gap-3 pt-2">
                    <label className="text-xs flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={!!editProduct.purchasable}
                        onChange={(e) =>
                          setEditProduct({
                            ...editProduct,
                            purchasable: e.target.checked,
                          })
                        }
                      />
                      Purchasable / Online Payment
                    </label>

                    <label className="text-xs flex items-center gap-2"><input type="checkbox" checked={!!editProduct.featured} disabled={(fixedProductType || editProduct.product_type) === "gift" && !editProduct.featured && products.filter((p) => p?.product_type === "gift" && p?.active !== false && p?.featured === true).length >= 6} onChange={(e) => setEditProduct({ ...editProduct, featured: e.target.checked })} /> {(fixedProductType || editProduct.product_type) === "gift" ? "Show in homepage Personalised Gifts (max 6)" : "Show on homepage (Picked for You)"}</label>
                    <label className="text-xs flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={!!editProduct.active}
                        onChange={(e) =>
                          setEditProduct({
                            ...editProduct,
                            active: e.target.checked,
                          })
                        }
                      />
                      Active
                    </label>
                  </div>
                </div>

                <div className="mt-6 flex gap-3 justify-end">
                  <button
                    type="button"
                    onClick={() => {
                      setEditProductSlug(null);
                      setEditProduct(null);
                    }}
                    className="btn-outline-pmt"
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    onClick={saveEditProduct}
                    className="btn-primary"
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
          )}

      </div>
    </>
  );
}
